// v2.0 — AYA PRO Accueil : refonte fidèle à la planche (sombre, masque doré, Business Desk, services).
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu, Search, Mic, Camera, FileText, Package, Receipt, Headphones, ArrowRight, ChevronDown,
  Home, LayoutGrid, Bookmark, User, Leaf, FileSpreadsheet, Smartphone, BarChart3,
} from "lucide-react";
import { getProProfile, setProProfile, setSpace, PROFILE_META } from "@/pro/proSpace";
import AyaMenu from "@/components/AyaMenu";
import { AyaProLogo } from "@/components/pro/AyaProLogo";

const API = process.env.REACT_APP_BACKEND_URL || "";

const QUICK = [
  { id: "devis", label: "Devis", Icon: FileText, color: "#34D399", to: "/pro/desk" },
  { id: "fournisseurs", label: "Fournisseurs", Icon: Package, color: "#F5A623", to: "/pro/categorie/fournisseurs" },
  { id: "taxes", label: "Taxes", Icon: Receipt, color: "#60A5FA", to: "/pro/taxes" },
  { id: "assistance", label: "Assistance", Icon: Headphones, color: "#F87171", to: "/pro/assistant" },
];
const DESK_MINIS = [
  { l: "DEVIS", Icon: FileText }, { l: "FACTURES", Icon: FileSpreadsheet },
  { l: "ENCAISSEMENTS", Icon: Smartphone }, { l: "SUIVI", Icon: BarChart3 },
];
const COMMERCANT_DESCS = {
  fournisseurs: { title: "Fournisseurs & Grossistes", desc: "Grossistes chinois vérifiés : électronique, textile, cosmétiques, BTP…" },
  "taxes-mairie": { title: "Taxes & Mairie", desc: "Redevances, patente, avis journaliers et reçus de votre commune." },
  juridique: { title: "Juridique", desc: "Contrats, litiges, conseils et documents officiels." },
  "finance-pro": { title: "Finance Pro", desc: "Crédits, comptes pro, solutions de paiement et financement." },
};
const MON_COMMERCE_TILES = [
  { to: "/pro/desk", Icon: FileText, label: "Business Desk", desc: "Devis, factures et reçus en FCFA" },
  { to: "/pro/compte", Icon: Receipt, label: "Compte & Banque Pro", desc: "Carte virtuelle, solde, mini-site" },
  { to: "/pro/catalogues", Icon: LayoutGrid, label: "Mes Catalogues", desc: "Créer et partager vos catalogues" },
  { to: "/pro/live", Icon: Smartphone, label: "AYA Live", desc: "Vendre en direct avec l'overlay" },
  { to: "/pro/fiscal", Icon: Receipt, label: "Fiscal Center", desc: "FNE, RNE, avoirs & journal fiscal" },
];

export default function AyaProHomePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(getProProfile());
  const [registry, setRegistry] = useState(null);
  const [taxDue, setTaxDue] = useState(null);
  const [profMenu, setProfMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const monCommerceRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/v3/pro-space/registry`).then((r) => r.json())
      .then((d) => { if (d?.ok) setRegistry(d); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (profile !== "commercant") return;
    const t = localStorage.getItem("civ_user_token");
    if (!t) return;
    fetch(`${API}/api/v3/taxes/account`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.json())
      .then((d) => { if (d?.ok && d.account) setTaxDue({ count: d.due_count, total: d.due_total_minor }); })
      .catch(() => {});
  }, [profile]);

  const profileData = registry?.profiles?.find((p) => p.id === profile);
  const categories = (profileData?.categories || []).filter((c) => c.id !== "mon-commerce");
  const shown = showAll ? categories : categories.slice(0, 4);
  const isCommercant = profile === "commercant";
  const isMunicipalite = profile === "municipalite";
  const meta = PROFILE_META[profile] || PROFILE_META.commercant;

  const switchProfile = (id) => { setProProfile(id); setProfile(id); setProfMenu(false); };
  const submitSearch = (e) => { e.preventDefault(); navigate(`/pro/assistant${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`); };
  const catRoute = (c) => (c.id === "taxes-mairie" ? "/pro/taxes" : `/pro/categorie/${c.id}`);

  return (
    <div style={S.page} data-testid="pro-home-page">
      <AyaMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* ── HEADER ── */}
      <header style={S.header}>
        <button style={S.iconBtn} data-testid="pro-menu-btn" aria-label="Menu" onClick={() => setMenuOpen(true)}><Menu size={18} /></button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center" }}>
            <AyaProLogo variant="hero" />
          </div>
          <div style={{ fontSize: 8.5, letterSpacing: 3.5, color: "#CFE5D6", fontWeight: 700, marginTop: 3 }}>L'AFRIQUE AU QUOTIDIEN</div>
          <div style={{ fontSize: 11, color: "#9DB4A6", fontWeight: 600, marginTop: 2 }}>Votre partenaire business en Côte d'Ivoire</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end", position: "relative" }}>
          <button style={S.chip} data-testid="pro-profile-chip" onClick={() => setProfMenu((o) => !o)}>
            {meta.icon} {meta.label} <ChevronDown size={12} />
          </button>
          <button style={{ ...S.chip, borderColor: "rgba(52,211,153,0.6)" }} data-testid="pro-switch-normal-btn" onClick={() => { setSpace("normal"); navigate("/"); }}>
            <Leaf size={12} color="#34D399" /> AYA
          </button>
          {profMenu && (
            <div style={S.profMenu} data-testid="pro-profile-menu">
              {Object.entries(PROFILE_META).map(([id, m]) => (
                <button key={id} style={{ ...S.profOpt, ...(id === profile ? { color: "#34D399" } : {}) }}
                  data-testid={`pro-profile-option-${id}`} onClick={() => switchProfile(id)}>{m.icon} {m.label}</button>
              ))}
            </div>
          )}
        </div>
      </header>

      <div style={S.container}>
        {/* ── RECHERCHE BUSINESS ── */}
        <form onSubmit={submitSearch} style={S.searchWrap} data-testid="pro-assistant-bar">
          <Search size={16} color="#9DB4A6" style={{ flexShrink: 0 }} />
          <input style={S.searchInput} data-testid="pro-search-input" placeholder="Posez votre question business à AYA PRO…"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <Mic size={16} color="#CFE5D6" style={{ flexShrink: 0 }} />
          <Camera size={16} color="#CFE5D6" style={{ flexShrink: 0 }} />
          <button type="submit" style={S.searchGo} data-testid="pro-search-go" aria-label="Rechercher"><Search size={16} color="#fff" /></button>
        </form>

        {/* ── 4 RACCOURCIS ── */}
        <div style={S.quickRow} data-testid="pro-quick-row">
          {QUICK.map((s) => (
            <button key={s.id} style={{ ...S.quick, borderColor: `${s.color}55`, background: `${s.color}12` }}
              data-testid={`pro-quick-${s.id}`} onClick={() => navigate(s.to)}>
              <s.Icon size={20} color={s.color} strokeWidth={2} />
              <span style={S.quickLabel}>{s.label}</span>
            </button>
          ))}
        </div>

        {/* ── BANNIÈRE BUSINESS DESK ── */}
        <div style={S.desk} data-testid="pro-desk-banner">
          <img src="/aya/pro/pro_desk_banner.jpg" alt="Business Desk" style={S.deskImg} />
          <div style={S.deskOv} />
          <span style={S.deskSign} aria-hidden="true">Mon commerce<br />plus simple<br />avec AYA</span>
          <div style={S.deskContent}>
            <h2 style={{ margin: 0, fontSize: 21, fontWeight: 900, color: "#fff" }}>Business Desk</h2>
            <p style={{ margin: "5px 0 10px", fontSize: 12, color: "#E8F6EC", fontWeight: 600, lineHeight: 1.35 }}>Devis, factures & reçus<br />de paiement en FCFA</p>
            <button style={S.deskBtn} data-testid="pro-desk-cta" onClick={() => navigate("/pro/desk")}>
              Accéder au Business Desk <ArrowRight size={14} />
            </button>
            <div style={{ display: "flex", gap: 13, marginTop: 12 }}>
              {DESK_MINIS.map((m) => (
                <span key={m.l} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                  <m.Icon size={15} color="#E8F6EC" />
                  <span style={{ fontSize: 6.8, fontWeight: 800, letterSpacing: 0.6, color: "#CFE5D6" }}>{m.l}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── SERVICES ── */}
        {isMunicipalite ? (
          <div style={{ ...S.card, marginTop: 16 }} data-testid="pro-municipalite-teaser">
            <span style={S.pilotTag}>Phase pilote</span>
            <h2 style={{ margin: "8px 0 6px", fontSize: 15, fontWeight: 900, color: "#fff" }}>AYA Municipalité</h2>
            <p style={{ margin: 0, fontSize: 11, color: "#9DB4A6", lineHeight: 1.5 }}>
              L'espace des 13 mairies du District d'Abidjan : tableau de bord des commerçants, avis de
              redevance journaliers, suivi du recouvrement et vérification des reçus par QR code.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
              {(registry?.communes || []).map((c) => (
                <span key={c} style={S.communePill} data-testid={`pro-commune-${c}`}>{c}</span>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div style={S.secHead}>
              <h2 style={S.secTitle}>Services {profileData?.label || "Commerçant"}</h2>
              <button style={S.secMore} data-testid="pro-services-more" onClick={() => setShowAll((v) => !v)}>
                {showAll ? "Voir moins" : "Voir plus"} <ArrowRight size={13} />
              </button>
            </div>
            <div style={S.grid} data-testid="pro-categories-grid">
              {shown.map((c) => {
                const ov = isCommercant ? COMMERCANT_DESCS[c.id] : null;
                return (
                  <button key={c.id} style={S.svcCard} data-testid={`pro-cat-${c.id}`} onClick={() => navigate(catRoute(c))}>
                    {c.id === "taxes-mairie" && taxDue && taxDue.count > 0 && (
                      <span style={S.dueBadge} data-testid="pro-tax-due-badge">{taxDue.count} j dus</span>
                    )}
                    <span style={S.svcTop}>
                      <span style={{ fontSize: 27, lineHeight: 1 }}>{c.icon}</span>
                      <span style={S.svcArrow}><ArrowRight size={13} color="#34D399" /></span>
                    </span>
                    <span style={S.svcTitle}>{ov?.title || c.label}</span>
                    <span style={S.svcDesc}>{ov?.desc || c.desc}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* ── MON COMMERCE ── */}
        {isCommercant && (
          <div ref={monCommerceRef} id="pro-mon-commerce">
            <div style={S.secHead}>
              <h2 style={S.secTitle}>Mon Commerce</h2>
            </div>
            <div style={S.grid} data-testid="pro-mon-commerce-grid">
              {MON_COMMERCE_TILES.map((t) => (
                <button key={t.to} style={S.svcCard} data-testid={`pro-tile-${t.to.replace(/\//g, "-")}`} onClick={() => navigate(t.to)}>
                  <span style={S.svcTop}>
                    <t.Icon size={21} color="#D4AF37" />
                    <span style={S.svcArrow}><ArrowRight size={13} color="#34D399" /></span>
                  </span>
                  <span style={S.svcTitle}>{t.label}</span>
                  <span style={S.svcDesc}>{t.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── NAV BASSE ── */}
      <nav style={S.nav} data-testid="pro-bottom-nav">
        {[
          { id: "accueil", Icon: Home, label: "Accueil", active: true, go: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
          { id: "rechercher", Icon: Search, label: "Rechercher", go: () => navigate("/pro/assistant") },
          { id: "outils", Icon: LayoutGrid, label: "Outils", go: () => (isCommercant ? monCommerceRef.current?.scrollIntoView({ behavior: "smooth" }) : navigate("/pro/desk")) },
          { id: "enregistres", Icon: Bookmark, label: "Enregistrés", go: () => navigate("/mon-compte") },
          { id: "profil", Icon: User, label: "Profil", go: () => navigate("/pro/compte") },
        ].map((t) => (
          <button key={t.id} data-testid={`pro-nav-${t.id}`} onClick={t.go}
            style={{ ...S.navItem, ...(t.active ? S.navOn : {}) }}>
            <t.Icon size={18} strokeWidth={t.active ? 2.4 : 2} />
            <span style={{ fontSize: 9.5, fontWeight: t.active ? 800 : 600 }}>{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "radial-gradient(ellipse at top, #08170F 0%, #050D08 65%, #030805 100%)", color: "#ECF5EE", paddingBottom: 84, overflowX: "hidden" },
  container: { maxWidth: 560, margin: "0 auto", padding: "0 14px", minWidth: 0 },
  header: { display: "flex", alignItems: "flex-start", gap: 8, padding: "12px 12px 6px", maxWidth: 560, margin: "0 auto" },
  iconBtn: { width: 36, height: 36, borderRadius: 11, border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.05)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  chip: { display: "inline-flex", alignItems: "center", gap: 5, height: 30, padding: "0 11px", borderRadius: 999, border: "1px solid rgba(212,175,55,0.5)", background: "rgba(8,23,15,0.8)", color: "#ECF5EE", fontSize: 10.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" },
  profMenu: { position: "absolute", top: 34, right: 0, zIndex: 60, background: "#0A2418", border: "1px solid rgba(16,185,129,0.4)", borderRadius: 13, padding: 5, display: "flex", flexDirection: "column", minWidth: 160, boxShadow: "0 10px 30px rgba(0,0,0,0.5)" },
  profOpt: { textAlign: "left", padding: "9px 11px", borderRadius: 9, background: "none", border: "none", color: "#ECF5EE", fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  searchWrap: { display: "flex", alignItems: "center", gap: 8, marginTop: 10, height: 46, padding: "0 6px 0 14px", borderRadius: 999, background: "rgba(8,23,15,0.9)", border: "1.5px solid rgba(16,185,129,0.4)" },
  searchInput: { flex: 1, background: "transparent", border: "none", outline: "none", color: "#ECF5EE", fontSize: 12.5, minWidth: 0 },
  searchGo: { width: 36, height: 36, borderRadius: "50%", border: "none", background: "linear-gradient(135deg,#10B981,#0E7A4F)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  quickRow: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 8, marginTop: 11 },
  quick: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "13px 4px 10px", borderRadius: 15, border: "1px solid", cursor: "pointer", color: "#ECF5EE" },
  quickLabel: { fontSize: 10.5, fontWeight: 800 },
  desk: { position: "relative", borderRadius: 20, overflow: "hidden", marginTop: 12, minHeight: 210, border: "1px solid rgba(16,185,129,0.3)" },
  deskImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "72% center" },
  deskOv: { position: "absolute", inset: 0, background: "linear-gradient(92deg, rgba(3,13,8,0.94) 30%, rgba(3,13,8,0.55) 55%, rgba(3,13,8,0.1))" },
  deskSign: { position: "absolute", top: 12, right: 12, fontFamily: "'Caveat', cursive", fontWeight: 700, fontSize: 15, lineHeight: 1, color: "#fff", textAlign: "right", transform: "rotate(-5deg)", textShadow: "0 1px 8px rgba(0,0,0,0.7)", pointerEvents: "none" },
  deskContent: { position: "relative", padding: "18px 14px 14px", maxWidth: "62%" },
  deskBtn: { display: "inline-flex", alignItems: "center", gap: 7, height: 38, padding: "0 15px", borderRadius: 999, border: "1.5px solid rgba(52,211,153,0.8)", background: "rgba(16,185,129,0.18)", color: "#A7F3D0", fontSize: 12, fontWeight: 800, cursor: "pointer", boxShadow: "0 0 18px rgba(16,185,129,0.25)" },
  secHead: { display: "flex", justifyContent: "space-between", alignItems: "center", margin: "17px 0 9px" },
  secTitle: { fontSize: 17, fontWeight: 900, margin: 0, color: "#FFFFFF" },
  secMore: { display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", color: "#34D399", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 10 },
  svcCard: { position: "relative", display: "flex", flexDirection: "column", gap: 6, padding: "13px 12px", borderRadius: 17, background: "linear-gradient(160deg, rgba(10,36,24,0.75), rgba(5,16,10,0.9))", border: "1px solid rgba(16,185,129,0.3)", cursor: "pointer", color: "#ECF5EE", textAlign: "left", minHeight: 128 },
  svcTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  svcArrow: { width: 26, height: 26, borderRadius: "50%", border: "1px solid rgba(52,211,153,0.55)", background: "rgba(16,185,129,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  svcTitle: { fontSize: 14, fontWeight: 800, color: "#fff", lineHeight: 1.2 },
  svcDesc: { fontSize: 10.5, color: "#9DB4A6", lineHeight: 1.4 },
  dueBadge: { position: "absolute", top: 9, right: 42, background: "#C0392B", color: "#fff", fontSize: 9.5, fontWeight: 800, padding: "3px 8px", borderRadius: 999 },
  card: { background: "rgba(10,36,24,0.7)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 17, padding: 14 },
  pilotTag: { display: "inline-block", padding: "3px 10px", borderRadius: 999, background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.5)", color: "#D4AF37", fontSize: 9.5, fontWeight: 800 },
  communePill: { padding: "4px 10px", borderRadius: 999, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.35)", color: "#CFE5D6", fontSize: 10, fontWeight: 700 },
  nav: { position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50, display: "flex", justifyContent: "space-around", padding: "8px 6px calc(10px + env(safe-area-inset-bottom))", background: "rgba(5,13,8,0.97)", backdropFilter: "blur(12px)", borderTop: "1px solid rgba(16,185,129,0.2)" },
  navItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 3, background: "none", border: "none", color: "#7E958A", cursor: "pointer", padding: "5px 10px", borderRadius: 14 },
  navOn: { color: "#fff", background: "#0E7A4F", padding: "5px 16px" },
};
